import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { BookMarked, LogOut, Menu, X } from "lucide-react";
import CountrySelector from "@/components/CountrySelector";
import LanguageSelector from "@/components/LanguageSelector";

export default function Nav() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const authed = user && typeof user === "object";
    const isAdmin = authed && (user.email || "").toLowerCase() === "ignacioglezdiaz@gmail.com";

    const handleLogout = async () => {
        setMenuOpen(false);
        await logout();
        navigate("/");
    };

    const navLinkClass = (path) => {
        const active = location.pathname === path ||
            (path === "/biblioteca" && (location.pathname === "/biblioteca" || location.pathname === "/leidos"));
        return `transition-colors ${active ? "text-[var(--accent)]" : "text-[var(--ink-muted)] hover:text-[var(--accent)]"}`;
    };

    const closeMenu = () => setMenuOpen(false);

    // Primary items shown to all (Clubs is public; non-admin sees /clubs-private)
    const primaryItems = [
        { to: "/blog", label: t("blog.nav"), testId: "nav-blog-link" },
        { to: "/clubs", label: t("clubs.nav"), testId: "nav-clubs-link" },
        { to: "/biblioteca", label: t("nav.library"), testId: "nav-library-link", authOnly: true },
    ];
    // Admin-only items (Marketplace remains hidden for public)
    const adminItems = [
        { to: "/marketplace/admin", label: t("marketplace.nav"), testId: "nav-marketplace-link" },
        { to: "/admin/blog", label: t("nav.admin_blog"), testId: "nav-admin-blog-link" },
        { to: "/admin", label: t("nav.admin"), testId: "nav-admin-link" },
    ];

    return (
        <header
            data-testid="main-nav"
            className="w-full border-b border-[var(--border)] bg-[var(--bg)]/85 backdrop-blur-sm sticky top-0 z-40"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-14 flex items-center justify-between py-4 md:py-5">
                {/* Brand */}
                <Link
                    to="/"
                    data-testid="nav-home-link"
                    className="flex items-center gap-2 text-[var(--ink)] shrink-0"
                    onClick={closeMenu}
                >
                    <BookMarked size={18} strokeWidth={1.5} />
                    <span
                        className="font-display text-xl md:text-2xl tracking-tight"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("nav.brand")}
                    </span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-6 text-sm">
                    {primaryItems
                        .filter((it) => !it.authOnly || authed)
                        .map((it) => (
                            <Link key={it.to} to={it.to} data-testid={it.testId} className={navLinkClass(it.to)}>
                                {it.label}
                            </Link>
                        ))}
                    {isAdmin && adminItems.map((it) => (
                        <Link key={it.to} to={it.to} data-testid={it.testId} className={navLinkClass(it.to)}>
                            {it.label}
                        </Link>
                    ))}
                    {isAdmin && (
                        <Link
                            to="/admin"
                            data-testid="nav-admin-link"
                            className="text-[var(--accent)] hover:opacity-80 transition-opacity uppercase tracking-widest text-[10px]"
                        >
                            {t("admin.nav_link", "Admin")}
                        </Link>
                    )}

                    <div className="flex items-center gap-3 pl-4 border-l border-[var(--border)]">
                        <LanguageSelector compact />
                        <CountrySelector compact />
                    </div>

                    {authed ? (
                        <button
                            data-testid="nav-logout-btn"
                            onClick={handleLogout}
                            className="flex items-center gap-1.5 text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors"
                            aria-label={t("nav.logout")}
                        >
                            <LogOut size={14} strokeWidth={1.5} />
                            <span>{t("nav.logout")}</span>
                        </button>
                    ) : (
                        <div className="flex items-center gap-3">
                            <Link
                                to="/acceder"
                                data-testid="nav-login-link"
                                className="text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors"
                            >
                                {t("nav.login")}
                            </Link>
                            <Link
                                to="/registrarse"
                                data-testid="nav-register-link"
                                className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-4 py-2 text-sm"
                            >
                                {t("nav.register")}
                            </Link>
                        </div>
                    )}
                </nav>

                {/* Mobile hamburger button */}
                <button
                    type="button"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label="Menu"
                    aria-expanded={menuOpen}
                    className="md:hidden p-2 -mr-2 text-[var(--ink)] hover:text-[var(--accent)] transition-colors"
                >
                    {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
                </button>
            </div>

            {/* Mobile drawer */}
            {menuOpen && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden border-t border-[var(--border)] bg-[var(--bg)] px-5 py-6 space-y-4 animate-fade-up"
                >
                    {primaryItems
                        .filter((it) => !it.authOnly || authed)
                        .map((it) => (
                            <Link
                                key={it.to}
                                to={it.to}
                                data-testid={`${it.testId}-mobile`}
                                onClick={closeMenu}
                                className={`block text-base ${navLinkClass(it.to)}`}
                            >
                                {it.label}
                            </Link>
                        ))}
                    {isAdmin && adminItems.map((it) => (
                        <Link
                            key={it.to}
                            to={it.to}
                            data-testid={`${it.testId}-mobile`}
                            onClick={closeMenu}
                            className={`block text-base ${navLinkClass(it.to)}`}
                        >
                            {it.label}
                        </Link>
                    ))}
                    {isAdmin && (
                        <Link
                            to="/admin"
                            data-testid="nav-admin-link-mobile"
                            onClick={closeMenu}
                            className="block text-[var(--accent)] uppercase tracking-widest text-xs"
                        >
                            {t("admin.nav_link", "Admin")}
                        </Link>
                    )}

                    <div className="flex items-center gap-4 pt-4 border-t border-[var(--border)]">
                        <LanguageSelector compact />
                        <CountrySelector compact />
                    </div>

                    {authed ? (
                        <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
                            <span className="text-xs text-[var(--ink-dim)] truncate mr-3">{user.email}</span>
                            <button
                                data-testid="nav-logout-btn-mobile"
                                onClick={handleLogout}
                                className="flex items-center gap-1.5 text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors text-sm"
                            >
                                <LogOut size={14} strokeWidth={1.5} />
                                <span>{t("nav.logout")}</span>
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3 pt-3 border-t border-[var(--border)]">
                            <Link
                                to="/acceder"
                                data-testid="nav-login-link-mobile"
                                onClick={closeMenu}
                                className="text-[var(--ink-muted)] hover:text-[var(--accent)] text-sm"
                            >
                                {t("nav.login")}
                            </Link>
                            <Link
                                to="/registrarse"
                                data-testid="nav-register-link-mobile"
                                onClick={closeMenu}
                                className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-4 py-2.5 text-sm text-center"
                            >
                                {t("nav.register")}
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </header>
    );
}
